/**
 * FO-438 B4 - DE betaalstatus die de klant op zijn factuur ziet.
 *
 * PROBLEEM (Ojah-testronde): het portaal toonde "Betaald" op facturen die de
 * klant nooit betaald had. Twee oorzaken, allebei intern-correcte gegevens die
 * richting de klant iets anders betekenen:
 *
 *  1. FACTORING. Finqle betaalt STREEP uit (`paidOut` /
 *     `INVOICE_PAID_TO_MERCHANT`) zodra het verzoek is goedgekeurd - los van de
 *     vraag of de debiteur al betaald heeft. De spiegel zet daarop
 *     `betaaldOp` + `status = BETAALD`, want intern IS de factuur dan financieel
 *     afgehandeld. Voor de klant is dat geen betaling: bij Ojah stond
 *     FA2026-20239 op "Betaald" terwijl Finqle het volle bedrag (EUR 4.980,14)
 *     nog als openstaand meldde.
 *  2. TWEE BRONNEN NAAST ELKAAR. De lijst toonde de interne factuurstatus
 *     (met waarde BETAALD) én een afgeleide betaalstatus, die elkaar konden
 *     tegenspreken.
 *
 * REGEL: "Betaald" betekent richting de klant uitsluitend "wij hebben uw
 * betaling ontvangen". Uitbetaling aan STREEP, gefactureerd-zijn of een interne
 * status leveren dat nooit op. De interne semantiek verandert NIET - alleen wat
 * de klant leest.
 *
 * Pure functie: geen DB, geen I/O, zodat lijst, detail en tests dezelfde
 * afleiding gebruiken.
 */

/** Wat de klant als betaalstatus leest. */
export type KlantBetaalStatus =
  | 'OPEN'
  | 'DEELS_BETAALD'
  | 'BETAALD'
  | 'VERVALLEN'
  | 'CREDITNOTA'

export interface KlantBetaalStatusInput {
  /** Interne factuurstatus (Prisma `InvoiceStatus`), bv. `VERZONDEN`/`BETAALD`. */
  status: string
  /** Interne betaald-markering (boekhouding of Finqle-uitbetaling). */
  betaaldOp: Date | string | null
  /**
   * Finqle: de DEBITEUR (onze klant) heeft betaald - `paidIn` /
   * `INVOICE_PAID_BY_DEBTOR`. Het enige Finqle-signaal dat een klantbetaling is.
   */
  finqleBetaaldDoorKlantOp: Date | string | null
  /**
   * Finqle: uitbetaling AAN STREEP - `paidOut` / `INVOICE_PAID_TO_MERCHANT`.
   * Factoring, nooit bewijs van een klantbetaling.
   */
  finqleBetaaldAanStreepOp: Date | string | null
  /** Finqle: wat er volgens Finqle nog openstaat; `null` = geen Finqle-spiegel. */
  finqleOpenstaand: number | null
  /** Factuurtotaal (voor de deelbetaling-vergelijking). */
  totaal: number
  /** Creditnota (eigen credit-flow of een negatieve Finqle-factuur). */
  isCreditnota: boolean
  /** Vervaldatum (ISO-8601) of `null` als die nog niet vaststaat. */
  vervaldatumIso: string | null
  nu: Date
}

export interface KlantBetaalStatusResultaat {
  status: KlantBetaalStatus
  /** Alleen gevuld bij een ECHTE klantbetaling; nooit de factoring-datum. */
  betaaldOp: string | null
  /** Restbedrag bij een deelbetaling; anders `null`. */
  openstaandBedrag: number | null
}

/**
 * Kalenderdag-vergelijking in Europe/Amsterdam (`en-CA` -> "yyyy-MM-dd", dus
 * string-sorteerbaar). De opslag is UTC; zonder deze conversie geeft de
 * dag-grens een off-by-one. Een factuur die VANDAAG vervalt is nog niet
 * vervallen.
 */
const NL_DAG = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Amsterdam',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

function naarIso(waarde: Date | string | null): string | null {
  if (waarde == null) return null
  if (waarde instanceof Date) return Number.isNaN(waarde.getTime()) ? null : waarde.toISOString()
  const d = new Date(waarde)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

function isVervallen(vervaldatumIso: string | null, nu: Date): boolean {
  if (!vervaldatumIso) return false
  const vervaldatum = new Date(vervaldatumIso)
  if (Number.isNaN(vervaldatum.getTime())) return false
  return NL_DAG.format(vervaldatum) < NL_DAG.format(nu)
}

/**
 * Leidt de klant-betaalstatus af. Volgorde (eerste match wint):
 *
 *  1. Creditnota            -> `CREDITNOTA` (een creditering is geen betaling).
 *  2. Finqle meldt een rest -> `DEELS_BETAALD` met het openstaande bedrag.
 *  3. Debiteur-betaling     -> `BETAALD` (`paidIn`, of Finqle-openstaand op 0).
 *  4. Eigen boeking         -> `BETAALD`, MAAR alleen als de factuur niet enkel
 *                              via factoring is afgeboekt.
 *  5. Vervaldatum voorbij   -> `VERVALLEN`.
 *  6. Anders                -> `OPEN`.
 */
export function klantFactuurBetaalStatus(
  input: KlantBetaalStatusInput,
): KlantBetaalStatusResultaat {
  const betaaldDoorKlant = naarIso(input.finqleBetaaldDoorKlantOp)
  const betaaldAanStreep = naarIso(input.finqleBetaaldAanStreepOp)
  const internBetaaldOp = naarIso(input.betaaldOp)

  // 1. Creditnota: eigen weergave, nooit als betaling (§4.2). Ook geen
  //    "vervallen": een klant hoeft een creditnota niet te betalen.
  if (input.isCreditnota) {
    return { status: 'CREDITNOTA', betaaldOp: null, openstaandBedrag: null }
  }

  // Alleen de FACTORING-uitbetaling is bekend: `betaaldOp`/`status = BETAALD`
  // komen dan uit die uitbetaling en zeggen niets over de debiteur.
  const alleenFactoring = betaaldAanStreep !== null && betaaldDoorKlant === null

  // 2. Deelbetaling volgens Finqle. Vergelijken op absolute bedragen: een
  //    negatief totaal hoort bij een creditering en is hierboven al afgevangen,
  //    maar de klem houdt de vergelijking robuust bij vreemde data.
  const openstaand = input.finqleOpenstaand
  if (openstaand != null && Number.isFinite(openstaand)) {
    const rest = Math.abs(openstaand)
    const totaal = Math.abs(input.totaal)
    if (rest > 0 && totaal > 0 && rest < totaal) {
      return { status: 'DEELS_BETAALD', betaaldOp: betaaldDoorKlant, openstaandBedrag: rest }
    }
    // 3a. Finqle meldt niets meer openstaand: de debiteur heeft afgerekend.
    if (rest === 0) {
      return { status: 'BETAALD', betaaldOp: betaaldDoorKlant ?? internBetaaldOp, openstaandBedrag: null }
    }
  }

  // 3b. Expliciete debiteur-betaling uit de Finqle-koppeling.
  if (betaaldDoorKlant) {
    return { status: 'BETAALD', betaaldOp: betaaldDoorKlant, openstaandBedrag: null }
  }

  // 4. Eigen boekhouding. Voor een factuur die NIET via Finqle-factoring loopt is
  //    de interne afboeking wél de registratie van de klantbetaling: STREEP
  //    boekt die pas af als het geld binnen is. Loopt de factuur wél via
  //    factoring en is er geen debiteur-betaling, dan zegt die afboeking niets.
  if (!alleenFactoring && (internBetaaldOp !== null || input.status === 'BETAALD')) {
    return { status: 'BETAALD', betaaldOp: internBetaaldOp, openstaandBedrag: null }
  }

  if (isVervallen(input.vervaldatumIso, input.nu)) {
    return { status: 'VERVALLEN', betaaldOp: null, openstaandBedrag: null }
  }
  return { status: 'OPEN', betaaldOp: null, openstaandBedrag: null }
}

/**
 * De DOCUMENT-status die de klant leest.
 *
 * De interne `BETAALD` is een boekhoudstatus, geen documentstatus, en hij mag
 * niet als tweede betaal-bron naast de afgeleide betaalstatus staan (§2.4). Voor
 * de klant is zo'n factuur simpelweg gefactureerd; of hij betaald is, zegt de
 * betaalstatus. Alle andere statussen gaan ongewijzigd door.
 */
export function klantFactuurDocumentStatus(status: string): string {
  return status === 'BETAALD' ? 'GEFACTUREERD' : status
}
